import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileDown } from 'lucide-react';
import useBrandName from '@/hooks/use-brand-name';
import header from '@/assets/images/parrainages.png'
import pdf from '@/assets/pdf/Reglement_Offre-Parrainage_APRIL-Moto_2024.pdf'
import AddUser from '@/assets/images/icons/add-user.svg'
import AvionEnPapier from '@/assets/images/icons/avion-en-papier.svg'
import Cadeau from '@/assets/images/icons/cadeau.svg'
import SmallSpinner from '@/components/shared/spinners/small-spinner';
import { useParrainages } from '@/hooks/api/use-parrainages';
import type { Parrainage } from '@/types/api/parrainage';
import SendParrainage from "@/pages/parrainages/parrainages-detail/send-parrainage";
import ParrainagesFAQ from "@/pages/parrainages/parrainages-faq";

const ParrainagesDetail = () => {
    const brandName = useBrandName();
    const parrainagesQuery = useParrainages();
    const parrainages: Parrainage[] = parrainagesQuery.data ?? [];

    const etapeParrainage = (parrainage: Parrainage) => {
        if(parrainage.en_cours_at !== null) {
            return <Badge variant="outline" className="border-green-500 text-green-600">Parrainage validé</Badge>
        } else if(parrainage.en_cours_at === null && parrainage.subscribed_at !== null) {
            return <Badge variant="outline" className="border-muted-foreground text-muted-foreground">Contrat en cours de validation</Badge>
        } else {
            if (/^\+?[1-10]\d{1,14}$/.test(String(parrainage.email))){
                return <Badge variant="outline" className="border-muted-foreground text-muted-foreground">SMS de parrainage envoyé</Badge>
            } else {
                return <Badge variant="outline" className="border-muted-foreground text-muted-foreground">Email de parrainage envoyé</Badge>
            }
        }
    }

    const iconFilter = "invert(40%) sepia(91%) saturate(1598%) hue-rotate(189deg) brightness(95%) contrast(89%)";

    if (parrainagesQuery.isPending) return <SmallSpinner />;

    return (
        <div className="mt-6 space-y-6">

            {/* Header image */}
            <div className="relative">
                <img src={header} alt={`Parrainages ${brandName}`} className="w-full rounded-lg object-cover max-h-[250px]" />
            </div>

            {/* Send parrainage card */}
            <Card data-tutorial="parrainages-card" className="-mt-16 relative z-10 max-w-xl mx-auto">
                <CardContent className="p-6 text-center">
                    <p className="text-lg font-semibold mb-6">
                        Parrainez vos proches et <strong className="text-primary">obtenez jusqu'à 150€*</strong>
                    </p>
                    <p className="text-sm text-muted-foreground">
                        Invitez vos proches à souscrire une assurance chez {brandName} et recevez 30€ en carte cadeau lorsque le contrat de votre filleul sera validé par {brandName}.
                    </p>
                    <div className="mt-6">
                        {parrainages.length >= 5
                            ? <p className="text-lg font-semibold">Félicitation vous avez le maximum de parrainages&nbsp;!</p>
                            : <SendParrainage />
                        }
                    </div>
                    <p className="text-xs italic text-muted-foreground text-left mt-6">*Dans la limite de 5 filleuls</p>
                </CardContent>
            </Card>

            {/* Comment ça fonctionne */}
            <div>
                <h2 className="text-xl font-bold mb-6">Comment ça fonctionne ?</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Card>
                        <CardContent className="p-6 text-center">
                            <div className="mb-4">
                                <img src={AvionEnPapier} alt="Envoyer une invitation" width={50} className="mx-auto" style={{filter: iconFilter}} />
                            </div>
                            <p className="text-sm"><strong>Envoyez une invitation</strong><br/>de parrainage à vos amis pour qu'ils fassent un devis</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-6 text-center">
                            <div className="mb-4">
                                <img src={AddUser} alt="Souscription du filleul" width={50} className="mx-auto" style={{filter: iconFilter}} />
                            </div>
                            <p className="text-sm"><strong>Vos amis souscrivent</strong><br/>Un contrat d'assurance via le lien</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-6 text-center">
                            <div className="mb-4">
                                <img src={Cadeau} alt="Gagnez 30€" width={50} className="mx-auto" style={{filter: iconFilter}} />
                            </div>
                            <p className="text-sm"><strong>Vous gagnez 30€ chacun</strong><br/>Vous recevez chacun une carte cadeau de 30€ dès validation du contrat de votre filleul par {brandName}</p>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Liste des parrainages */}
            <Card>
                <CardContent className="p-6">
                    <p className="font-semibold mb-6">Vous pouvez parrainer jusqu'à 5 filleuls et obtenir 150€ de carte cadeau :</p>
                    <div className="space-y-3">
                        {parrainages.length === 0 ? (
                            <p className="text-sm text-muted-foreground">Aucun filleul invité</p>
                        ) : (
                            parrainages.map((p, i) => (
                                <div key={i}>
                                    <div className="flex items-center justify-between">
                                        <p className="font-semibold text-sm mb-0">{p.email}</p>
                                        {etapeParrainage(p)}
                                    </div>
                                    {i < parrainages.length - 1 && <hr className="mt-4 border-border" />}
                                </div>
                            ))
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Conditions */}
            <div>
                <h2 className="text-xl font-bold mb-6">Conditions de l'offre</h2>
                <Card>
                    <CardContent className="p-6">
                        <ul className="space-y-2 text-sm text-muted-foreground list-disc list-inside">
                            <li>Le Parrain et le Filleul ne peuvent être une seule et même personne</li>
                            <li>L'attribution des récompenses est effective une fois la souscription du contrat du filleul validée par {brandName} après réception des pièces justificatives</li>
                            <li>Le contrat du filleul doit être un contrat Moto ou Scooter (supérieur à <span aria-hidden="true">50 cm3</span><span className="sr-only">50 centimètres cubes</span>)</li>
                        </ul>
                        <Button variant="outline" asChild className="mt-6 cursor-pointer whitespace-normal py-6 text-center">
                            <a href={pdf} target="_blank" rel="noopener noreferrer" className="no-underline">
                                <FileDown className="w-4 h-4 shrink-0" />
                                Téléchargez les conditions de l'offre
                            </a>
                        </Button>
                    </CardContent>
                </Card>
            </div>

            {/* FAQ */}
            <div className="max-w-2xl mx-auto">
                <ParrainagesFAQ />
            </div>
        </div>
    );
};

export default ParrainagesDetail;
